import {
  LitElement,
  html,
  noChange,
  nothing,
  svg,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { customElement, property } from "lit/decorators.js";
import { consume } from "@lit/context";
import { createActorState, type ActorStateOf } from "../../state/actor-state";
import { live } from "lit/directives/live.js";
import { applyAccentVars } from "../../theme/theme";
import { purchaseCheckoutStyles } from "./purchase-checkout/purchase-checkout.styles";
import type { EmbedStripe } from "../../config/types";
import {
  validatePromotionCode,
  promoDiscount,
  fetchMemberContext,
  updateMemberPhone,
  fetchAppointmentForm,
  fetchFormsOnFile,
  submitAppointmentForm,
  type FormSubmitIdentity,
  type MemberContext,
  type EmbedFamilyMemberWithRoster,
  type EmbedForm,
  type FormQuestion,
} from "../../data/live";
import * as checkoutMath from "../../checkout/checkout-math";
import type { RequiredSignupFields } from "../../config/types";
import {
  emptyRequiredProfile,
  isPhoneRequired,
  missingRequiredProfileFields,
  requiredProfilePayload,
  resolveRequiredSignupFields,
  REQUIRED_PROFILE_ERRORS,
  dateOfBirthError,
} from "../../checkout/required-profile";
import {
  GIFT_MAX_CENTS,
  GIFT_MIN_CENTS,
  isFixedGiftAmount,
} from "../data/mock-shop";
import {
  completeGiftCard,
  createGiftPurchaseIntent,
  createGuestRoster,
  isAccountExistsError,
  runFreePurchase,
  type GuestRosterHandle,
  type GiftPurchaseIntent,
} from "../../payments/stripe-client";
import {
  accountExistsForOtherMessage,
  accountExistsMessage,
} from "../../checkout/account-exists-copy";
import { CheckoutSubmissionController } from "../../checkout/submission.machine";
import "./zipper-payment";
import type { ZipperPayment } from "./zipper-payment";
import "./zipper-confirmation";
import { openSignIn } from "./open-signin";
import { clearEmbedToken } from "../../config/client";
import {
  createPublicEmbedRuntime,
  embedRuntimeContext,
  runtimeApiArguments,
  runtimeApiOption,
  type EmbedRuntime,
} from "../../runtime/context";
import { formatCents, type OrderTotals } from "../../checkout/order-totals";
import { renderPurchaseSummary } from "./purchase-checkout/purchase-checkout.views";
import { renderIntakeQuestion } from "./booking-checkout/booking-checkout.views";
import { toParagraphs } from "./booking-checkout/booking-checkout.model";
import {
  DEFAULT_TERMS_PARAGRAPHS,
  RECIPIENT_OPTIONS,
  REFERRAL_OPTIONS,
  formatPurchaseMoney as fmtMoney,
  parseGiftAmountCents,
  type GiftDelivery,
  type RecipientType,
} from "./purchase-checkout/purchase-checkout.model";

export { parseGiftAmountCents } from "./purchase-checkout/purchase-checkout.model";

const ICON_PEOPLE = svg`<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
const ICON_DOC = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>`;
const ICON_CHEVRON = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_INFO = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
const ICON_SIGNIN = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;

const purchaseCheckoutActorState = createActorState(() => ({
  activeStep: 1,
  step1Done: false,
  step1Touched: false,
  buyer: { firstName: "", lastName: "", email: "", phone: "" },
  requiredProfile: emptyRequiredProfile(),
  forSomeoneElse: false,
  recipientType: "" as RecipientType,
  recipient: {
    firstName: "",
    lastName: "",
    dob: "",
    email: "",
    phone: "",
  },
  giftAmount: "",
  giftRecipient: { firstName: "", lastName: "", email: "" },
  giftMessage: "",
  giftDelivery: "email" as GiftDelivery,
  referral: "",
  promoCode: "",
  discount: 0,
  appliedCouponId: "",
  promoError: "",
  promoBusy: false,
  termsOpen: false,
  termsForm: null as EmbedForm | null,
  formLoadState: "idle" as "idle" | "loading" | "ready" | "error",
  formAnswers: {} as Record<string, string>,
  formTouched: new Set<string>(),
  formSubmitted: false,
  formOnFile: false,
  formOnFileLoading: false,
  formResponseId: null as string | null,
  isPreparingPayment: false,
  payError: "",
  signedInName: "",
  memberCtx: null as MemberContext | null,
  familyMembers: [] as EmbedFamilyMemberWithRoster[],
  selectedFamilyRosterId: 0,
  payReady: false,
  quote: null as OrderTotals | null,
  deferredPreview: null as
    import("@zipper/embedded-api-client").DeferredMembershipPreviewDto | null,
}));

type PurchaseCheckoutActorState = ActorStateOf<
  typeof purchaseCheckoutActorState
>;

/**
 * Two-step purchase checkout (Your details -> Payment) — ported from the
 * handoff `intro-pack-checkout.jsx`. Opened inline by <zipper-shop> when a
 * product is bought. Reuses the booking-checkout visual language (carded steps,
 * sticky Order Summary) and mounts <zipper-payment> for the real charge.
 * Recipient (family/guest) is collected and passed through the charge
 * description.
 */
@customElement("zipper-purchase-checkout")
export class ZipperPurchaseCheckout extends LitElement {
  @consume({ context: embedRuntimeContext, subscribe: true })
  @property({ attribute: false })
  runtime: EmbedRuntime = createPublicEmbedRuntime();
  private readonly submission = new CheckoutSubmissionController(
    this,
    "product",
  );

  static styles = purchaseCheckoutStyles;

  @property({ type: String }) productName = "Product";
  @property({ type: String }) productSub = "";
  @property({ type: Number }) price = 0;
  @property({ type: String }) cadence = "one-time";
  @property({ type: String }) image = "";
  @property({ attribute: false }) features: string[] = [];
  @property({ attribute: false })
  deferredChargeSettings?: import("@zipper/embedded-api-client").DeferredChargeSettingsDto;
  @property({ type: String }) stripeProductId = "";
  /** F3: id of the form (waiver/terms) assigned to this pricing option. 0 = none. */
  @property({ type: Number }) formId = 0;
  @property({ attribute: false }) stripe?: EmbedStripe;
  @property({ type: Number }) orgId = 0;
  @property({ type: String }) accent = "#4F9DB5";

  protected willUpdate(changed: PropertyValues): void {
    // Reflect the configured accent onto this host so `var(--zui-accent*)` used
    // inside the checkout (and the light-DOM payment element it renders) matches
    // the embed's accent instead of the baseStyles teal default. See X1.
    if (changed.has("accent")) applyAccentVars(this, this.accent);
    if (changed.has("isGift")) {
      this.submission.setKind(this.isGift ? "gift" : "product");
    }
  }
  @property({ type: String }) orgSlug = "";
  @property({ type: Boolean }) hideTerms = false;
  /** ST3: require a valid phone in "Your details" before advancing to payment. */
  @property({ type: Boolean }) requirePhone = false;
  /**
   * PLAT-4456 org policy (`EmbedConfig.requiredSignupFields`), enforced by the
   * API at `create-guest`. Collected in step 1 so a guest buyer is not rejected
   * at the card step for data we could have asked for up front.
   */
  @property({ attribute: false }) requiredSignupFields?: RequiredSignupFields;
  /** Org ISO-4217 currency (`EmbedConfig.currency`); USD when unset. */
  @property({ type: String }) currency = "";
  /** Hide the "purchasing for someone else" (family/friend) panel. */
  @property({ type: Boolean }) hideRecipientBooking = false;
  @property({ type: Boolean }) firstTimeClientsOnly = false;
  /**
   * This product is a gift card (`category: 'giftCards'` / `custom-amount`),
   * so checkout runs the gift sequence — purchase-intent → charge the returned
   * product/amount → in-app-complete — instead of an ordinary product charge.
   *
   * Set by the shop from the PRODUCT. There is deliberately no buyer-facing
   * "make this a gift" option: an ordinary membership or pack can never be
   * turned into a gift card.
   */
  @property({ type: Boolean }) isGift = false;

  /**
   * The server's price for this cart — the tax it will assess and the platform
   * fee the org passes on. Handed over by `<zipper-payment>`, which previews
   * the charge it is about to make, so the summary and the payment step can
   * never disagree. Null until the buyer reaches payment.
   */
  @purchaseCheckoutActorState.field("deferredPreview")
  private deferredPreview!:
    import("@zipper/embedded-api-client").DeferredMembershipPreviewDto | null;
  @purchaseCheckoutActorState.field("quote")
  private quote!: PurchaseCheckoutActorState["quote"];

  @purchaseCheckoutActorState.field("activeStep")
  private activeStep!: PurchaseCheckoutActorState["activeStep"];

  @purchaseCheckoutActorState.field("step1Done")
  private step1Done!: PurchaseCheckoutActorState["step1Done"];

  @purchaseCheckoutActorState.field("step1Touched")
  private step1Touched!: PurchaseCheckoutActorState["step1Touched"];

  @purchaseCheckoutActorState.field("buyer")
  private buyer!: PurchaseCheckoutActorState["buyer"];

  @purchaseCheckoutActorState.field("requiredProfile")
  private requiredProfile!: PurchaseCheckoutActorState["requiredProfile"];

  @purchaseCheckoutActorState.field("forSomeoneElse")
  private forSomeoneElse!: PurchaseCheckoutActorState["forSomeoneElse"];

  @purchaseCheckoutActorState.field("recipientType")
  private recipientType!: PurchaseCheckoutActorState["recipientType"];

  @purchaseCheckoutActorState.field("recipient")
  private recipient!: PurchaseCheckoutActorState["recipient"];

  /** Buyer-typed gift amount in dollars. Unused for fixed-denomination cards. */
  @purchaseCheckoutActorState.field("giftAmount")
  private giftAmount!: PurchaseCheckoutActorState["giftAmount"];

  @purchaseCheckoutActorState.field("giftRecipient")
  private giftRecipient!: PurchaseCheckoutActorState["giftRecipient"];

  @purchaseCheckoutActorState.field("giftMessage")
  private giftMessage!: PurchaseCheckoutActorState["giftMessage"];

  @purchaseCheckoutActorState.field("giftDelivery")
  private giftDelivery!: PurchaseCheckoutActorState["giftDelivery"];
  /**
   * The gift card row created by `/gift-cards/purchase-intent`, together with
   * the inputs it was created from. Cached so re-opening step 2 (back → next)
   * reuses the same pending GiftCard instead of minting a fresh one per visit;
   * a changed amount/recipient invalidates it and a new intent is created.
   */
  private giftIntent: GiftPurchaseIntent | null = null;
  private giftIntentKey = "";

  @purchaseCheckoutActorState.field("referral")
  private referral!: PurchaseCheckoutActorState["referral"];

  @purchaseCheckoutActorState.field("promoCode")
  private promoCode!: PurchaseCheckoutActorState["promoCode"];

  @purchaseCheckoutActorState.field("discount")
  private discount!: PurchaseCheckoutActorState["discount"];

  @purchaseCheckoutActorState.field("appliedCouponId")
  private appliedCouponId!: PurchaseCheckoutActorState["appliedCouponId"];

  @purchaseCheckoutActorState.field("promoError")
  private promoError!: PurchaseCheckoutActorState["promoError"];

  @purchaseCheckoutActorState.field("promoBusy")
  private promoBusy!: PurchaseCheckoutActorState["promoBusy"];

  @purchaseCheckoutActorState.field("termsOpen")
  private termsOpen!: PurchaseCheckoutActorState["termsOpen"];

  /** The authored form assigned to this pricing option. */
  @purchaseCheckoutActorState.field("termsForm")
  private termsForm!: PurchaseCheckoutActorState["termsForm"];

  @purchaseCheckoutActorState.field("formLoadState")
  private formLoadState!: PurchaseCheckoutActorState["formLoadState"];

  @purchaseCheckoutActorState.field("formAnswers")
  private formAnswers!: PurchaseCheckoutActorState["formAnswers"];

  @purchaseCheckoutActorState.field("formTouched")
  private formTouched!: PurchaseCheckoutActorState["formTouched"];

  @purchaseCheckoutActorState.field("formSubmitted")
  private formSubmitted!: PurchaseCheckoutActorState["formSubmitted"];

  @purchaseCheckoutActorState.field("formOnFile")
  private formOnFile!: PurchaseCheckoutActorState["formOnFile"];

  @purchaseCheckoutActorState.field("formOnFileLoading")
  private formOnFileLoading!: PurchaseCheckoutActorState["formOnFileLoading"];

  @purchaseCheckoutActorState.field("formResponseId")
  private formResponseId!: PurchaseCheckoutActorState["formResponseId"];

  @purchaseCheckoutActorState.field("isPreparingPayment")
  private isPreparingPayment!: PurchaseCheckoutActorState["isPreparingPayment"];

  /** Semantic submit/commit/recovery state belongs to the XState statechart. */
  private get placed(): boolean {
    return this.submission.succeeded;
  }

  private get submitting(): boolean {
    return this.submission.busy;
  }

  @purchaseCheckoutActorState.field("payError")
  private payError!: PurchaseCheckoutActorState["payError"];

  @purchaseCheckoutActorState.field("signedInName")
  private signedInName!: PurchaseCheckoutActorState["signedInName"];

  /** Signed-in member context (saved cards). Null = guest. */
  @purchaseCheckoutActorState.field("memberCtx")
  private memberCtx!: PurchaseCheckoutActorState["memberCtx"];

  @purchaseCheckoutActorState.field("familyMembers")
  private familyMembers!: PurchaseCheckoutActorState["familyMembers"];

  @purchaseCheckoutActorState.field("selectedFamilyRosterId")
  private selectedFamilyRosterId!: PurchaseCheckoutActorState["selectedFamilyRosterId"];

  /**
   * Mirrors <zipper-payment>'s `canSubmit`, fed by its `zipper-pay-ready`
   * event. The card fields live in Stripe's cross-origin iframe, so this event
   * is the only way to know the embedded form is actually chargeable — hence
   * the CTA greys out until it says so.
   */
  @purchaseCheckoutActorState.field("payReady")
  private payReady!: PurchaseCheckoutActorState["payReady"];

  private paymentEl: ZipperPayment | null = null;
  private formLoadVersion = 0;
  private formGuestRoster: GuestRosterHandle | null = null;
  private formGuestIdentityKey = "";
  private formResponseIdentityKey = "";
  private formSavePromise: Promise<string | null> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadMember();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (
      (changed.has("formId") ||
        changed.has("orgId") ||
        changed.has("isGift"))
    ) {
      void this.loadTermsForm();
    }
    if (
      changed.has("activeStep") &&
      this.activeStep === 2 &&
      (this.total > 0 || this.deferredChargeSettings?.enabled) &&
      !this.paymentEl
    ) {
      void this.updateComplete.then(() => this.mountInlinePayment());
    }
    if (changed.has("activeStep") && this.activeStep !== 2) {
      this.teardownPayment();
    }
    // B5: only re-anchor the scroll position on a REAL step transition, not
    // on initial mount (where `changed.get('activeStep')` is still
    // `undefined` — the property hasn't changed FROM anything yet).
    if (changed.has("activeStep") && changed.get("activeStep") !== undefined) {
      this.scrollActiveStepIntoView();
    }
  }

  /**
   * B5: move the newly-active step card into view instead of letting the
   * browser fall back to scrolling the HOST page to the top. That fallback
   * happens because the outgoing step's `.body` (which held the focused
   * "Next"/"Edit" button) unmounts on transition — removing the focused
   * element from the DOM makes the browser reset focus (and, with it, the
   * scroll position) to the top of the page. `setActiveStep()` blurs first
   * to avoid triggering that; this then explicitly scrolls the right node.
   * `scrollIntoView`, called on an element inside THIS shadow root, still
   * walks up through the shadow boundary and scrolls the real host-page
   * scroller, so this works correctly despite the component living in a
   * shadow root (naive `window.scrollTo(0, 0)` would instead just scroll
   * the top-level window regardless of where the step card actually is).
   */
  private scrollActiveStepIntoView(): void {
    const card = this.renderRoot.querySelector(".step.open");
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  /** B5: change step, blurring first so the browser doesn't lose focus to a
   * detached node (see `scrollActiveStepIntoView`). */
  private setActiveStep(step: number): void {
    (this.shadowRoot?.activeElement as HTMLElement | null)?.blur();
    this.activeStep = step;
  }

  /** Load the assigned form. A configured requirement fails closed. */
  private async loadTermsForm(): Promise<void> {
    const version = ++this.formLoadVersion;
    const orgId = this.orgId;
    const formId = this.formId;
    this.formSubmitted = false;
    this.formOnFile = false;
    this.formOnFileLoading = false;
    this.formResponseId = null;
    this.formResponseIdentityKey = "";
    if (!orgId || !formId || this.isGift) {
      this.termsForm = null;
      this.formLoadState = "idle";
      return;
    }
    this.formLoadState = "loading";
    const form = await fetchAppointmentForm(orgId, formId);
    if (
      version !== this.formLoadVersion ||
      orgId !== this.orgId ||
      formId !== this.formId
    ) {
      return;
    }
    if (!form) {
      this.termsForm = null;
      this.formLoadState = "error";
      return;
    }
    this.termsForm = form;
    this.formLoadState = "ready";
    this.termsOpen = true;
    if (this.memberCtx) await this.loadOnFileResponse(formId, version);
  }

  private async loadOnFileResponse(
    formId: number,
    version = this.formLoadVersion,
  ): Promise<void> {
    if (!this.memberCtx || !formId) return;
    this.formOnFileLoading = true;
    const responses = await fetchFormsOnFile(this.orgId, [formId]);
    if (version !== this.formLoadVersion || formId !== this.formId) return;
    this.formOnFile = responses.has(formId);
    this.formResponseId = responses.get(formId) ?? null;
    if (this.formOnFile) {
      this.formResponseIdentityKey = this.currentFormIdentityKey;
    }
    this.formOnFileLoading = false;
  }

  /** Pick up an existing customer session (no-op for guests; null on failure). */
  private async loadMember(): Promise<void> {
    if (!this.orgId) return;
    const ctx = await fetchMemberContext(this.orgId);
    if (!ctx) return;
    if (!this.memberCtx || this.memberCtx.rosterId !== ctx.rosterId) {
      this.formOnFile = false;
      this.formResponseId = null;
      this.formResponseIdentityKey = "";
      this.formGuestRoster = null;
      this.formGuestIdentityKey = "";
    }
    this.memberCtx = ctx;
    this.familyMembers = ctx.familyMembers ?? [];
    this.signedInName = ctx.firstName?.trim() || "there";
    if (this.formId) {
      await this.loadOnFileResponse(this.formId);
    }
    if (ctx.email || ctx.firstName) {
      this.buyer = {
        ...this.buyer,
        firstName: this.buyer.firstName || ctx.firstName || "",
        lastName: this.buyer.lastName || ctx.lastName || "",
        email: this.buyer.email || ctx.email || "",
        phone:
          this.buyer.phone ||
          (ctx.phone ? checkoutMath.formatPhoneInput(ctx.phone) : ""),
      };
    }
  }

  private logout(): void {
    clearEmbedToken(this.orgId);
    this.memberCtx = null;
    this.familyMembers = [];
    this.selectedFamilyRosterId = 0;
    this.signedInName = "";
    this.formOnFile = false;
    this.formResponseId = null;
    this.formResponseIdentityKey = "";
    this.formGuestRoster = null;
    this.formGuestIdentityKey = "";
  }

  private get recurring(): boolean {
    if (this.isGift) return false;
    return checkoutMath.isRecurringCadence(this.cadence);
  }

  /**
   * A gift card whose catalog price is a real denomination ($50) is simply
   * charged at that price. One priced below the server's $5 floor (the $0/$1
   * "Enter your price!" placeholders) cannot be a sellable value, so the buyer
   * is asked for the amount. See `isFixedDenominationGift` in `mock-shop.ts`.
   */
  private get giftFixedCents(): number {
    if (!isFixedGiftAmount(this.price)) return 0;
    return Math.round((this.price || 0) * 100);
  }
  /** Gift mode where the buyer must supply the amount. */
  private get giftAsksAmount(): boolean {
    return this.isGift && this.giftFixedCents === 0;
  }
  /** The gift's value in cents: the fixed denomination, or what the buyer typed. */
  private get giftCents(): number {
    if (!this.isGift) return 0;
    if (this.giftFixedCents) return this.giftFixedCents;
    return parseGiftAmountCents(this.giftAmount, this.currency).cents;
  }
  /** `sendToRecipient` for the purchase-intent + in-app-complete calls. */
  private get giftSendToRecipient(): boolean {
    return this.giftDelivery === "email";
  }

  private get subtotal(): number {
    // In gift mode the catalog price is either the fixed denomination or a
    // placeholder the buyer has replaced — either way `giftCents` is the truth.
    if (this.isGift) return this.giftCents / 100;
    return this.price;
  }
  private get total(): number {
    return checkoutMath.applyDiscount(this.subtotal, this.discount);
  }

  /**
   * What the CTA promises the buyer. Once <zipper-payment> has a server quote
   * this is the charge itself — tax and any passed-on platform fee included —
   * because a button that says "$99" has no business charging $110.68. Until
   * then it is the cart's own arithmetic, qualified by the summary's "Taxes
   * and fees calculated at checkout".
   */
  private get amountDue(): string {
    return this.quote
      ? formatCents(this.quote.totalInCents, this.currency)
      : fmtMoney(this.total, this.currency);
  }

  /** The per-month figure for a membership: what recurs, not the first bill. */
  private get recurringDue(): string {
    return this.quote?.recurringTotalInCents
      ? formatCents(this.quote.recurringTotalInCents, this.currency)
      : this.amountDue;
  }

  // B1: single source of truth for step-1 validity, shared with `step1Errors()`
  // below so the Next-button gate and the visible messages can never drift.
  private get detailsValid(): boolean {
    return this.step1Errors().length === 0;
  }

  /**
   * The embed's own `requirePhone` option ORed with the org's
   * `requiredSignupFields.phoneNumber` — only the latter is enforced by the API.
   */
  private get phoneRequired(): boolean {
    return isPhoneRequired(this.requirePhone, this.requiredSignupFields);
  }

  /**
   * Whether this purchase provisions its own roster through `create-guest`. A
   * signed-in member already has one, so the org's required-signup-fields
   * policy has nothing to attach to and is not re-asked of them.
   */
  private get isGuestCheckout(): boolean {
    return !this.memberCtx;
  }

  /** A configured product form remains authoritative even when generic terms are hidden. */
  private get termsRequired(): boolean {
    return !this.isGift && !!this.formId;
  }

  private get productFormValid(): boolean {
    if (!this.termsRequired || this.formOnFile || !!this.formResponseId) {
      return true;
    }
    if (this.memberCtx && this.formOnFileLoading) return false;
    if (this.formLoadState !== "ready" || !this.termsForm) return false;
    return this.termsForm.questions.every(
      (question) => !this.formQuestionError(question),
    );
  }

  private identityAnswerFor(question: FormQuestion): string | null {
    const value =
      question.type === "first-name"
        ? this.buyer.firstName
        : question.type === "last-name"
          ? this.buyer.lastName
          : question.type === "email"
            ? this.buyer.email
            : question.type === "phone-number"
              ? this.buyer.phone
              : "";
    return value.trim() || null;
  }

  private formAnswerKey(question: FormQuestion): string {
    return `${this.formId}:${question.key}`;
  }

  private formAnswerFor(question: FormQuestion): string {
    return (
      this.identityAnswerFor(question) ??
      this.formAnswers[this.formAnswerKey(question)] ??
      ""
    ).trim();
  }

  private multiSelectValues(raw: string): string[] {
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (value): value is string => typeof value === "string",
        );
      }
    } catch {
      // Preserve older single-value answers instead of discarding them.
    }
    return [raw];
  }

  private formQuestionError(question: FormQuestion): string | null {
    if (!question.required) return null;
    const value = this.formAnswerFor(question);
    if (question.type === "multi-select") {
      return this.multiSelectValues(value).length
        ? null
        : "At least one item must be selected.";
    }
    if (question.type === "single-select") {
      return value ? null : "One item must be selected.";
    }
    if (question.type === "email") {
      return checkoutMath.isValidEmail(value)
        ? null
        : "An email address is required.";
    }
    if (question.type === "date") {
      return value ? null : "A date must be selected.";
    }
    return value ? null : "This field is required.";
  }

  private touchFormQuestion(key: string): void {
    if (this.formTouched.has(key)) return;
    this.formTouched = new Set([...this.formTouched, key]);
  }

  private renderProductFormQuestion(
    question: FormQuestion,
  ): TemplateResult | typeof nothing {
    if (this.identityAnswerFor(question) !== null) return nothing;
    const key = this.formAnswerKey(question);
    const error = this.formQuestionError(question);
    return renderIntakeQuestion(question, {
      value: this.formAnswers[key] ?? "",
      error,
      showError:
        !!error && (this.formTouched.has(key) || this.formSubmitted),
      onWrite: (value) => {
        if (this.formAnswers[key] !== value) {
          this.formResponseId = null;
          this.formResponseIdentityKey = "";
        }
        this.formAnswers = { ...this.formAnswers, [key]: value };
        this.payError = "";
      },
      onTouch: () => this.touchFormQuestion(key),
      parseMultiSelect: (value) => this.multiSelectValues(value),
    });
  }

  private formSubmissionValue(question: FormQuestion): string {
    const value = this.formAnswerFor(question);
    if (question.type !== "date" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const local = new Date(`${value}T00:00:00`);
    return Number.isNaN(local.getTime()) ? value : local.toISOString();
  }

  private async focusFirstInvalidFormQuestion(): Promise<void> {
    await this.updateComplete;
    const field = this.renderRoot.querySelector<HTMLElement>(
      ".fld.invalid input, .fld.invalid textarea, .fld.invalid select, .opt-chips.invalid button",
    );
    field?.focus();
  }

  private async createFormGuestRoster(): Promise<GuestRosterHandle> {
    const identityKey = this.currentFormIdentityKey;
    if (
      this.formGuestRoster &&
      this.formGuestIdentityKey === identityKey
    ) {
      return this.formGuestRoster;
    }
    this.formGuestRoster = null;
    this.formResponseId = null;
    this.formResponseIdentityKey = "";
    try {
      this.formGuestRoster = await createGuestRoster(
        this.orgId,
        {
          ...this.buyer,
          ...requiredProfilePayload(this.requiredProfile),
        },
        ...runtimeApiArguments(this.runtime),
      );
      this.formGuestIdentityKey = identityKey;
      return this.formGuestRoster;
    } catch (error) {
      if (isAccountExistsError(error)) {
        this.payError = accountExistsMessage(this.buyer.email);
      } else {
        this.payError = "Could not set up your customer profile. Please try again.";
      }
      throw new Error(this.payError);
    }
  }

  private async saveProductFormResponse(
    payment?: ZipperPayment,
  ): Promise<string | null> {
    const form = this.termsForm;
    if (!form) throw new Error("The required purchase form is not available.");
    const identityKey = this.currentFormIdentityKey;
    let identity: FormSubmitIdentity;
    if (this.memberCtx) {
      identity = { kind: "member" };
    } else {
      const guest =
        payment?.guestRoster ??
        this.formGuestRoster ??
        (await payment?.ensureGuestRoster()) ??
        (await this.createFormGuestRoster());
      this.formGuestRoster = guest;
      this.formGuestIdentityKey = identityKey;
      if (payment) payment.guestRoster = guest;
      identity = {
        kind: "guest",
        rosterId: guest.rosterId,
        checkoutToken: guest.checkoutToken,
      };
    }
    if (identityKey !== this.currentFormIdentityKey) {
      throw new Error("Your details changed while saving. Review the form and try again.");
    }
    const answers = form.questions.map((question) => ({
      questionKey: question.key,
      value: this.formSubmissionValue(question),
    }));
    const responseId = await submitAppointmentForm(
      this.orgId,
      form.id,
      answers,
      identity,
    );
    if (!responseId) {
      throw new Error("The required purchase form could not be saved. Please try again.");
    }
    if (form.id !== this.formId || identityKey !== this.currentFormIdentityKey || answers.some((answer, index) =>
      answer.value !== this.formSubmissionValue(form.questions[index]!),
    )) throw new Error("Your details changed while saving. Review the form and try again.");
    this.formResponseId = responseId;
    this.formResponseIdentityKey = this.currentFormIdentityKey;
    return responseId;
  }

  private get currentFormIdentityKey(): string {
    if (this.memberCtx) return `member:${this.memberCtx.rosterId}`;
    return `guest:${JSON.stringify({
      buyer: this.buyer,
      requiredProfile: this.requiredProfile,
    })}`;
  }

  private invalidateStaleFormIdentity(): void {
    const identityKey = this.currentFormIdentityKey;
    if (
      this.formResponseIdentityKey &&
      this.formResponseIdentityKey !== identityKey
    ) {
      this.formResponseId = null;
      this.formResponseIdentityKey = "";
      this.formOnFile = false;
    }
    if (
      !this.memberCtx &&
      this.formGuestIdentityKey &&
      this.formGuestIdentityKey !== identityKey
    ) {
      this.formGuestRoster = null;
      this.formGuestIdentityKey = "";
    }
  }

  private async ensureProductFormResponse(
    payment?: ZipperPayment,
  ): Promise<string | undefined> {
    if (!this.termsRequired) return undefined;
    this.invalidateStaleFormIdentity();
    if (this.formOnFile) return this.formResponseId ?? undefined;
    if (this.formResponseId) return this.formResponseId;
    if (!this.productFormValid) {
      this.formSubmitted = true;
      this.termsOpen = true;
      this.payError = "Complete the required purchase form to continue.";
      await this.focusFirstInvalidFormQuestion();
      throw new Error(this.payError);
    }
    if (!this.formSavePromise) {
      this.formSavePromise = this.saveProductFormResponse(payment)
        .catch((error: unknown) => {
          this.payError =
            error instanceof Error
              ? error.message
              : "The required purchase form could not be saved. Please try again.";
          throw error;
        })
        .finally(() => {
          this.formSavePromise = null;
        });
    }
    const responseId = await this.formSavePromise;
    this.payError = "";
    return responseId ?? undefined;
  }

  private bindProductFormResponse(
    payment: ZipperPayment,
    responseId: string | undefined,
  ): void {
    if (responseId) {
      payment.items = payment.items.map((item) =>
        item.stripeProductId === this.stripeProductId
          ? { ...item, formResponseId: responseId }
          : item,
      );
    }
    if (this.formGuestRoster) payment.guestRoster = this.formGuestRoster;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownPayment();
  }

  private close(): void {
    this.dispatchEvent(
      new CustomEvent("zipper-purchase-close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async applyPromo(): Promise<void> {
    if (this.deferredChargeSettings?.enabled) return;
    const code = this.promoCode.trim();
    if (!code || this.promoBusy) return;
    this.promoBusy = true;
    this.promoError = "";
    try {
      const r = await validatePromotionCode(this.orgId, {
        code,
        stripeProductId: this.stripeProductId || undefined,
        customerEmail: this.buyer.email || undefined,
        orderAmountInCents: Math.round(this.subtotal * 100),
      });
      if (!r.valid) {
        this.discount = 0;
        this.appliedCouponId = "";
        this.promoError = r.errorMessage || "That code isn’t valid.";
        return;
      }
      this.appliedCouponId = r.couponId ?? "";
      this.discount = promoDiscount(r, this.subtotal);
    } catch {
      this.discount = 0;
      this.appliedCouponId = "";
      this.promoError = "Could not check that code. Try again.";
    } finally {
      this.promoBusy = false;
    }
  }

  // ── Render ──

  render(): TemplateResult {
    if (this.placed) {
      // A gift is confirmed as a gift: whose it is, and how it reaches them —
      // not "book a class to use your purchase", which the buyer cannot do.
      const isGift = this.isGift;
      const subline =
        this.deferredChargeSettings?.enabled && this.deferredPreview
          ? `Membership reserved · First charge: ${this.deferredPreview.expectedChargeLabel} · ${formatCents(this.deferredPreview.futureChargeAmountInCents, this.currency)}`
          : isGift
            ? `Gift card for ${this.giftRecipientName}`
            : this.productSub;
      const nextLine = isGift
        ? this.giftSendToRecipient
          ? `We’ve emailed ${this.giftRecipientName} at ${this.giftRecipient.email} with their claim link.`
          : `Nothing was sent to ${this.giftRecipientName} — check your email for the claim details to pass on.`
        : "Next: book a class or appointment from the schedule to use your purchase.";
      return html`<div>
        <zipper-confirmation
          kind="purchase"
          .accent=${this.accent}
          .orgId=${this.orgId}
          .email=${this.buyer.email}
          .firstName=${this.buyer.firstName}
          .lastName=${this.buyer.lastName}
          .cTitle=${isGift
            ? `Gift card · ${fmtMoney(this.giftCents / 100, this.currency)}`
            : this.productName}
          .subline=${subline}
          .price=${
            this.deferredChargeSettings?.enabled
              ? `Due today ${formatCents(0, this.currency)}`
              : this.total > 0
                ? fmtMoney(this.total, this.currency)
                : "Free"
          }
          .hideSetupAccount=${!!this.memberCtx}
          .hideBookAnother=${false}
          @zipper-confirm-book-another=${() => this.close()}
        ></zipper-confirmation>
        <p class="step-sum" style="text-align:center;margin:1rem 0 0">
          ${nextLine}
        </p>
      </div>`;
    }
    return html`
      <div class="page">
        <button class="back" @click=${this.close}>← Back to shop</button>
        <div class="grid">
          <div class="col">${this.renderStep1()} ${this.renderStep2()}</div>
          ${this.renderSummary()}
        </div>
      </div>
    `;
  }

  /**
   * 3b: an IME composition (Japanese/Chinese/Korean candidate entry) is in
   * flight in one of the fields below. Not actor-backed — flipping it must not
   * itself schedule a render.
   */
  private composing = false;

  private field(
    label: string,
    value: string,
    type: string,
    on: (v: string) => void,
    required = false,
    accessibility?: { id: string; descriptionId?: string; invalid?: boolean; autocomplete?: string },
  ): TemplateResult {
    // B1: `.value` uses `live()`, not a plain binding. formatPhoneInput()
    // truncates overflow, so past the 10-digit cap the recomputed string
    // equals the previously-rendered one; lit's PropertyPart dirty-checks
    // against its OWN cache, skips the write, and the raw un-truncated
    // keystrokes the browser already put in the DOM stay visible while state
    // is correctly capped. live() compares against the real DOM property
    // every render, forcing the re-sync.
    //
    // 3b: that same re-sync destroys an in-progress IME composition — lit
    // re-renders the WHOLE template on any reactive change, so a keystroke in
    // one field makes live() overwrite the half-composed candidate text in
    // another. Both halves of the round trip have to stand down while the IME
    // owns the element: `noChange` leaves the DOM property untouched, and the
    // `@input` guard keeps the intermediate composition buffer out of state
    // (browsers fire `input` with `isComposing: true` for every candidate
    // update, and letting those through would make even a plain, non-`live`
    // binding write the buffer straight back into the element).
    return html`<label class=${`fld ${accessibility?.invalid ? "invalid" : ""}`}
      >${label}${required ? " *" : ""}
      <input
        type=${type}
        id=${accessibility?.id ?? nothing}
        aria-invalid=${accessibility?.invalid ? "true" : nothing}
        aria-describedby=${accessibility?.descriptionId ?? nothing}
        autocomplete=${accessibility?.autocomplete ?? nothing}
        ?required=${required}
        .value=${this.composing ? noChange : live(value)}
        @compositionstart=${() => (this.composing = true)}
        @compositionend=${(e: Event) => this.endComposition(e, on)}
        @input=${(e: Event) => {
          if ((e as InputEvent).isComposing) return;
          on((e.target as HTMLInputElement).value);
        }}
      />
    </label>`;
  }

  /**
   * 3b: commit the composed text once the IME hands the element back. `input`
   * ordering around `compositionend` is browser-dependent (Chrome fires it
   * after, Safari before), so read the value straight off the element instead
   * of waiting for a follow-up `input`. The explicit `requestUpdate()`
   * guarantees one render with `live()` back in play even when the composed
   * text happens to equal what state already held, re-syncing any field whose
   * write was skipped while composing.
   */
  /**
   * Date of birth / emergency contact, when the ORG requires them (PLAT-4456).
   * Guests only — a signed-in member's roster already carries this data.
   */
  private renderRequiredProfileFields(): TemplateResult | typeof nothing {
    if (!this.isGuestCheckout) return nothing;
    const required = resolveRequiredSignupFields(this.requiredSignupFields);
    const dobError = this.requiredProfile.dateOfBirth
      ? dateOfBirthError(this.requiredSignupFields, this.requiredProfile.dateOfBirth)
      : null;
    if (!required.dateOfBirth && !required.emergencyContact) return nothing;
    const patch = (field: keyof typeof this.requiredProfile, value: string) => {
      this.requiredProfile = { ...this.requiredProfile, [field]: value };
    };
    return html`
      ${required.dateOfBirth
        ? html`<div class="signup-age-group">
            ${this.field(
              "Date of birth",
              this.requiredProfile.dateOfBirth,
              "date",
              (v) => patch("dateOfBirth", v),
              true,
              {
                id: "purchase-date-of-birth",
                descriptionId: dobError || this.requiredSignupFields?.minimumAge != null
                  ? "purchase-birthday-guidance" : undefined,
                invalid: !!dobError,
                autocomplete: "bday",
              },
            )}
            ${dobError || this.requiredSignupFields?.minimumAge != null
              ? html`<p id="purchase-birthday-guidance" role=${dobError ? "alert" : nothing}
                  class=${dobError ? "fld-err" : "signup-age-help"}>
                  ${dobError || `You must be ${this.requiredSignupFields?.minimumAge} or older to sign up.`}
                </p>`
              : nothing}
          </div>`
        : nothing}
      ${required.emergencyContact
        ? html`<div class="grid2">
            ${this.field(
              "Emergency contact name",
              this.requiredProfile.emergencyContactName,
              "text",
              (v) => patch("emergencyContactName", v),
              true,
            )}
            ${this.field(
              "Emergency contact phone",
              this.requiredProfile.emergencyContactPhone,
              "tel",
              (v) => patch("emergencyContactPhone", v),
              true,
            )}
          </div>`
        : nothing}
    `;
  }

  private endComposition(e: Event, on: (v: string) => void): void {
    this.composing = false;
    on((e.target as HTMLInputElement).value);
    this.requestUpdate();
  }

  // B1: exhaustive validation for every field in step 1 — buyer AND (when
  // purchasing for someone else) recipient. Backs both the visible error
  // list and the Next-button disabled state (via `detailsValid`), so no
  // required field can be left unguarded.
  /**
   * B1: `step1Touched` is only set by the Next button's click handler, but
   * that button is `?disabled=${!detailsValid}` — a disabled button fires no
   * click, so the error list could never render at exactly the moment it was
   * needed. The visitor just saw a dead Next button with no explanation.
   *
   * Reveal the errors as soon as they have actually entered something invalid,
   * while staying quiet about pristine, still-empty fields.
   */
  private get step1ShowErrors(): boolean {
    if (this.step1Touched) return true;
    const badPhone = (p: string): boolean =>
      !!p.trim() && !checkoutMath.hasValidPhone(p);
    const badEmail = (e: string): boolean =>
      !!e.trim() && !checkoutMath.isValidEmail(e);
    return (
      badPhone(this.buyer.phone) ||
      badEmail(this.buyer.email) ||
      (this.isGift &&
        (badEmail(this.giftRecipient.email) ||
          (!!this.giftAmount.trim() &&
            !!parseGiftAmountCents(this.giftAmount, this.currency).error))) ||
      (this.forSomeoneElse &&
        (badPhone(this.recipient.phone) || badEmail(this.recipient.email)))
    );
  }

  /**
   * Gift-specific step-1 problems. Everything the API would reject is caught
   * here first, so the buyer never reaches the payment step (and no GiftCard
   * row is created) with an amount or recipient the server will refuse.
   */
  private giftErrors(): string[] {
    const errs: string[] = [];
    if (!this.isGift) return errs;
    if (this.giftAsksAmount) {
      const { error } = parseGiftAmountCents(this.giftAmount, this.currency);
      if (error) errs.push(error);
    }
    const r = this.giftRecipient;
    if (!r.firstName.trim() || !r.lastName.trim()) {
      errs.push("Enter the gift recipient's first and last name.");
    }
    // `sendToRecipient: true` makes recipientEmail mandatory server-side; when
    // the buyer is delivering the card themselves it is genuinely optional.
    if (this.giftSendToRecipient) {
      if (!checkoutMath.isValidEmail(r.email)) {
        errs.push("Enter the gift recipient's email address.");
      }
    } else if (r.email.trim() && !checkoutMath.isValidEmail(r.email)) {
      errs.push("Enter a valid gift recipient email, or leave it blank.");
    }
    if (this.giftMessage.length > 500) {
      errs.push("Keep the gift message under 500 characters.");
    }
    return errs;
  }

  private step1Errors(): string[] {
    const errs: string[] = [];
    if (!this.buyer.firstName.trim() || !this.buyer.lastName.trim()) {
      errs.push("Enter your first and last name.");
    }
    if (!checkoutMath.isValidEmail(this.buyer.email)) {
      errs.push("Enter a valid email address.");
    }
    if (this.phoneRequired && !checkoutMath.hasValidPhone(this.buyer.phone)) {
      errs.push("Enter a valid phone number; include + and a country code if needed.");
    } else if (checkoutMath.hasInvalidOptionalPhone(this.buyer.phone)) {
      errs.push("Enter a valid phone number (include + and a country code if needed) or leave the field blank.");
    }
    // Org-required signup fields, named individually — a single "complete your
    // profile" is unactionable when the studio requires three separate things.
    if (this.isGuestCheckout) {
      for (const missing of missingRequiredProfileFields(
        this.requiredSignupFields,
        this.requiredProfile,
      )) {
        errs.push(missing === 'dateOfBirth'
          ? dateOfBirthError(this.requiredSignupFields, this.requiredProfile.dateOfBirth) || REQUIRED_PROFILE_ERRORS.dateOfBirth
          : REQUIRED_PROFILE_ERRORS[missing]);
      }
    }
    errs.push(...this.giftErrors());
    if (this.forSomeoneElse) {
      if (!this.recipientType) {
        errs.push("Choose who this purchase is for (family or guest).");
      } else if (
        !(this.recipientType === "family" && this.selectedFamilyRosterId > 0)
      ) {
        const r = this.recipient;
        if (!r.firstName.trim() || !r.lastName.trim()) {
          errs.push("Enter the recipient's first and last name.");
        }
        if (
          this.recipientType !== "family" &&
          !checkoutMath.isValidEmail(r.email)
        ) {
          errs.push("Enter the recipient's email address.");
        }
        if (checkoutMath.hasInvalidOptionalPhone(r.phone)) {
          errs.push(
            "Enter a valid recipient phone number (include + and a country code if needed) or leave it blank.",
          );
        }
      }
    }
    return errs;
  }

  private patchBuyer(k: keyof typeof this.buyer, v: string): void {
    const val = k === "phone" ? checkoutMath.formatPhoneInput(v) : v;
    this.buyer = { ...this.buyer, [k]: val };
  }
  private patchRecipient(k: keyof typeof this.recipient, v: string): void {
    if (this.recipientType === "family" && this.selectedFamilyRosterId > 0) {
      this.selectedFamilyRosterId = 0;
    }
    const val = k === "phone" ? checkoutMath.formatPhoneInput(v) : v;
    this.recipient = { ...this.recipient, [k]: val };
  }

  private renderStep1(): TemplateResult {
    const open = this.activeStep === 1;
    const summary = this.step1Done
      ? `${`${this.buyer.firstName} ${this.buyer.lastName}`.trim()} · ${this.buyer.email}`
      : "";
    return html`
      <div class="card step ${open ? "open" : ""}">
        <div
          class="step-head"
          @click=${() => this.step1Done && this.setActiveStep(1)}
        >
          <span
            class="num ${open ? "open" : ""} ${this.step1Done && !open
              ? "done"
              : ""}"
            >${this.step1Done && !open ? ICON_CHECK : 1}</span
          >
          <span class="step-title">Your details</span>
          ${open ? this.renderSignInCta() : nothing}
          ${this.step1Done && !open
            ? html`<button class="edit" @click=${() => this.setActiveStep(1)}>
                Edit
              </button>`
            : nothing}
        </div>
        ${this.step1Done && !open
          ? html`<div class="step-sum">${summary}</div>`
          : nothing}
        ${open
          ? html`<div class="body">
              ${this.renderSignInBanner()}
              <div class="grid2">
                ${this.field(
                  "First name",
                  this.buyer.firstName,
                  "text",
                  (v) => this.patchBuyer("firstName", v),
                  true,
                )}
                ${this.field(
                  "Last name",
                  this.buyer.lastName,
                  "text",
                  (v) => this.patchBuyer("lastName", v),
                  true,
                )}
              </div>
              <div class="grid2">
                ${this.field(
                  "Email",
                  this.buyer.email,
                  "email",
                  (v) => this.patchBuyer("email", v),
                  true,
                )}
                ${this.field(
                  "Phone",
                  this.buyer.phone,
                  "tel",
                  (v) => this.patchBuyer("phone", v),
                  this.phoneRequired,
                )}
              </div>
              ${this.renderRequiredProfileFields()}
              <div class="note">
                ${ICON_INFO} If this email matches an existing client account,
                this purchase will be added to that account automatically.
              </div>
              ${this.isGift ? this.renderGiftDetails() : nothing}
              ${this.isGift || this.hideRecipientBooking
                ? nothing
                : this.renderRecipient()}

                <label class="fld referral"
                  >How did you hear about us?
                  <select
                    class="fld-select"
                    @change=${(e: Event) =>
                      (this.referral = (e.target as HTMLSelectElement).value)}
                  >
                    <option value="">Select an option</option>
                    ${REFERRAL_OPTIONS.map(
                      (o) =>
                        html`<option
                          value=${o}
                          ?selected=${o === this.referral}
                        >
                          ${o}
                        </option>`,
                    )}
                  </select>
                </label>

                ${
                  this.step1ShowErrors && this.step1Errors().length
                    ? html`<div class="promo-msg err">
                        ${this.step1Errors().map((e) => html`<div>${e}</div>`)}
                      </div>`
                    : nothing
                }
                ${this.renderTerms()}
                ${this.payError ? html`<div class="promo-msg err" role="alert">${this.payError}</div>` : nothing}
                <button
                  class="cta right"
                  ?disabled=${!this.detailsValid || !this.productFormValid || this.isPreparingPayment}
                  aria-busy=${this.isPreparingPayment ? "true" : "false"}
                  @click=${() => void this.advanceToPayment()}
                >
                  ${this.isPreparingPayment ? "Saving…" : "Next"}
                </button>
              </div>`
            : nothing
        }
      </div>
    `;
  }

  private selectFamilyMember(m: EmbedFamilyMemberWithRoster): void {
    this.selectedFamilyRosterId = m.rosterId;
    this.recipient = {
      ...this.recipient,
      firstName: m.firstName,
      lastName: m.lastName,
    };
  }

  private clearFamilySelection(): void {
    this.selectedFamilyRosterId = 0;
  }

  /**
   * Pick the recipient kind. The options are mutually exclusive — `recipientType`
   * holds exactly one value — so they render as a native radio group (see
   * `.radio-box`) and this handler simply overwrites the previous choice, which
   * is what clears the other option. Switching kinds drops any saved family
   * member that was picked under the old one.
   */
  private chooseRecipientType(type: Exclude<RecipientType, "">): void {
    if (this.recipientType === type) return;
    this.recipientType = type;
    this.clearFamilySelection();
  }

  private async syncMemberPhone(): Promise<void> {
    if (!this.orgId || !this.memberCtx || !this.buyer.phone) return;
    if (!checkoutMath.hasValidPhone(this.buyer.phone)) return;
    try {
      await updateMemberPhone(this.orgId, this.buyer.phone);
    } catch {
      /* non-blocking */
    }
  }

  private patchGiftRecipient(
    k: keyof typeof this.giftRecipient,
    v: string,
  ): void {
    this.giftRecipient = { ...this.giftRecipient, [k]: v };
    // Recipient identity is part of the intent's cache key; a new one is
    // created on the next payment mount (see `ensureGiftIntent`).
    this.giftIntent = null;
  }

  /**
   * Gift-card details: the amount (only when the card has no real
   * denomination), who it is for, and how it reaches them. Replaces the
   * "purchasing for someone else" panel — a gift card already IS for someone
   * else, and it grants a redeemable balance rather than assigning a product
   * to another roster.
   */
  private renderGiftDetails(): TemplateResult {
    const r = this.giftRecipient;
    const emailing = this.giftSendToRecipient;
    return html`
      <div class="others on">
        <div class="recipient">
          <h5>Gift card details</h5>
          ${this.giftAsksAmount
            ? html`<div class="grid2">
                ${this.field(
                  "Amount (USD)",
                  this.giftAmount,
                  "text",
                  (v) => {
                    this.giftAmount = v;
                    this.giftIntent = null;
                  },
                  true,
                )}
                <div class="help" style="align-self:end">
                  Any amount from
                  ${fmtMoney(GIFT_MIN_CENTS / 100, this.currency)} to
                  ${fmtMoney(GIFT_MAX_CENTS / 100, this.currency)}.
                </div>
              </div>`
            : html`<div class="note">
                ${ICON_INFO} This gift card is worth
                ${fmtMoney(this.giftFixedCents / 100, this.currency)}.
              </div>`}
          <div class="grid2">
            ${this.field(
              "Recipient first name",
              r.firstName,
              "text",
              (v) => this.patchGiftRecipient("firstName", v),
              true,
            )}
            ${this.field(
              "Recipient last name",
              r.lastName,
              "text",
              (v) => this.patchGiftRecipient("lastName", v),
              true,
            )}
          </div>
          <div
            class="scenario-row"
            role="radiogroup"
            aria-label="How should we deliver this gift card?"
          >
            <label class="radio-box">
              <input
                type="radio"
                name="gift-delivery"
                .checked=${live(emailing)}
                @change=${() => this.setGiftDelivery("email")}
              />
              Email it to them
            </label>
            <label class="radio-box">
              <input
                type="radio"
                name="gift-delivery"
                .checked=${live(!emailing)}
                @change=${() => this.setGiftDelivery("self")}
              />
              Send it to me to give
            </label>
          </div>
          ${this.field(
            emailing ? "Recipient email" : "Recipient email (optional)",
            r.email,
            "email",
            (v) => this.patchGiftRecipient("email", v),
            emailing,
          )}
          ${this.field("Message (optional)", this.giftMessage, "text", (v) => {
            this.giftMessage = v;
            this.giftIntent = null;
          })}
          <div class="help">
            ${emailing
              ? "We’ll email them a claim link as soon as your payment goes through."
              : "Nothing is sent to the recipient — the claim details come to you to pass on."}
          </div>
        </div>
      </div>
    `;
  }

  private setGiftDelivery(mode: GiftDelivery): void {
    if (this.giftDelivery === mode) return;
    this.giftDelivery = mode;
    this.giftIntent = null;
  }

  private renderRecipient(): TemplateResult {
    const sub = this.forSomeoneElse
      ? "Pick who this purchase is for."
      : "Add a family member or friend.";
    const isFamily = this.recipientType === "family";
    return html`
      <div class="others ${this.forSomeoneElse ? "on" : ""}">
        <button
          class="others-toggle"
          @click=${() => (this.forSomeoneElse = !this.forSomeoneElse)}
        >
          <span class="ic">${ICON_PEOPLE}</span>
          <span>
            <span class="t">Buying for someone else?</span>
            <span class="s">${sub}</span>
          </span>
          <span class="switch ${this.forSomeoneElse ? "on" : ""}"></span>
        </button>
        ${this.forSomeoneElse
          ? html`
              <div
                class="scenario-row"
                role="radiogroup"
                aria-label="Who is this purchase for?"
              >
                ${RECIPIENT_OPTIONS.map(
                  (o) =>
                    html`<label class="radio-box">
                      <input
                        type="radio"
                        name="recipient-type"
                        .value=${o.value}
                        .checked=${live(this.recipientType === o.value)}
                        @change=${() => this.chooseRecipientType(o.value)}
                      />
                      ${o.label}
                    </label>`,
                )}
              </div>
              ${this.recipientType
                ? html`<div class="recipient">
                    <h5>${isFamily ? "Family member" : "Guest"}</h5>
                    ${isFamily && this.familyMembers.length
                      ? html`<div class="fm-list">
                          ${this.familyMembers.map(
                            (m) =>
                              html`<button
                                type="button"
                                class="fm-opt ${this.selectedFamilyRosterId ===
                                m.rosterId
                                  ? "sel"
                                  : ""}"
                                @click=${() => this.selectFamilyMember(m)}
                              >
                                <span>${m.firstName} ${m.lastName}</span>
                                ${this.selectedFamilyRosterId === m.rosterId
                                  ? ICON_CHECK
                                  : nothing}
                              </button>`,
                          )}
                        </div>`
                      : isFamily && !this.familyMembers.length
                        ? html`<div class="fm-empty">
                            No family members on your account yet — add their
                            details below.
                          </div>`
                        : nothing}
                    ${isFamily && this.familyMembers.length
                      ? html`<p class="fm-manual-t">Or add someone new</p>`
                      : nothing}
                    <div
                      class="${isFamily && this.familyMembers.length
                        ? "fm-manual"
                        : ""}"
                    >
                      <div class="grid2">
                        ${this.field(
                          "First name",
                          this.recipient.firstName,
                          "text",
                          (v) => this.patchRecipient("firstName", v),
                        )}
                        ${this.field(
                          "Last name",
                          this.recipient.lastName,
                          "text",
                          (v) => this.patchRecipient("lastName", v),
                        )}
                      </div>
                      ${isFamily
                        ? this.field(
                            "Date of birth",
                            this.recipient.dob,
                            "date",
                            (v) => this.patchRecipient("dob", v),
                          )
                        : nothing}
                      <div class="grid2">
                        ${this.field(
                          isFamily ? "Email (optional)" : "Email",
                          this.recipient.email,
                          "email",
                          (v) => this.patchRecipient("email", v),
                        )}
                        ${this.field(
                          "Phone (optional)",
                          this.recipient.phone,
                          "tel",
                          (v) => this.patchRecipient("phone", v),
                        )}
                      </div>
                      <div class="help">${this.recipientHelp()}</div>
                    </div>
                  </div>`
                : nothing}
            `
          : nothing}
      </div>
    `;
  }

  private recipientHelp(): string {
    if (this.recipientType === "family")
      return "We'll save them to your account so you can book for them with one tap next time.";
    return "Your guest will have their own account for future bookings, but will be linked to yours as a guest as well.";
  }

  private async advanceToPayment(): Promise<void> {
    this.step1Touched = true;
    if (!this.detailsValid || !this.productFormValid || this.isPreparingPayment) return;
    this.isPreparingPayment = true;
    this.payError = "";
    try {
      await this.ensureProductFormResponse();
      void this.syncMemberPhone();
      this.step1Done = true;
      this.setActiveStep(2);
    } catch (error) {
      this.payError = error instanceof Error ? error.message : "Could not save your form. Please try again.";
    } finally {
      this.isPreparingPayment = false;
    }
  }

  /**
   * Open the shared customer sign-in modal and refresh the member context once
   * the customer is in. Single owner of the sign-in entry point for this
   * checkout so the headline CTA can never drift from the old inline link's
   * behaviour.
   */
  private openSignInModal(): void {
    openSignIn(this.orgId, this.accent, (name) => {
      this.signedInName = name || "there";
      void this.loadMember();
    });
  }

  /**
   * 7/25 QA: the sign-in affordance is a real (secondary) button sitting on the
   * "Your details" headline row, not a link buried in the microcopy under it.
   * Signed-out only — once the customer is in, {@link renderSignInBanner}
   * carries the "Signed in as …" status + Log out inside the step body, where
   * it explains the prefilled fields.
   */
  private renderSignInCta(): TemplateResult | typeof nothing {
    if (this.signedInName) return nothing;
    return html`<div
      class="signin-cta"
      @click=${(e: Event) => e.stopPropagation()}
    >
      <span class="signin-cta-t">Already a customer?</span>
      <button
        type="button"
        class="zui-btn-secondary"
        title="Sign in to prefill your details and pay with a card on file"
        @click=${() => this.openSignInModal()}
      >
        ${ICON_SIGNIN} Sign in
      </button>
    </div>`;
  }

  /** Signed-in status + Log out, above the prefilled buyer fields. */
  private renderSignInBanner(): TemplateResult | typeof nothing {
    if (!this.signedInName) return nothing;
    return html`<div class="note" style="margin-bottom:0.75rem">
      Signed in as ${this.signedInName}.
      <button type="button" class="edit" @click=${() => this.logout()}>
        Log out
      </button>
    </div>`;
  }

  private renderStep2(): TemplateResult {
    const open = this.activeStep === 2;
    // A gift card is never free: step 1 will not advance until the amount
    // clears the server's $5 floor, so the $0 path can't apply here.
    const isFree =
      !this.isGift && !this.deferredChargeSettings?.enabled && this.total <= 0;
    const due = this.amountDue;
    const cta = this.deferredChargeSettings?.enabled
      ? "Save card and reserve membership"
      : isFree
        ? "Confirm purchase · Free"
        : this.isGift
          ? `Buy gift card · ${due}`
          : this.recurring
            ? `Start membership · ${this.recurringDue}/mo`
            : `Buy ${this.productName} · ${due}`;
    return html`
      <div class="card step ${open ? "open" : ""}">
        <div
          class="step-head"
          @click=${() => this.step1Done && void this.advanceToPayment()}
        >
          <span class="num ${open ? "open" : ""}">2</span>
          <span class="step-title"
            >${isFree ? "Confirm purchase" : "Payment"}</span
          >
        </div>
        ${open
          ? html`<div class="body">
              <p class="step-sum" style="margin:0">
                ${isFree
                  ? "Review and confirm your free purchase below."
                  : "Enter your payment details below, then confirm your purchase."}
              </p>
              ${!isFree
                ? html`<div id="inline-pay-host" class="inline-pay"></div>`
                : nothing}
              ${this.payError
                ? html`<div class="promo-msg err">${this.payError}</div>`
                : nothing}
              <button
                class="cta block"
                ?disabled=${!this.productFormValid ||
                this.submitting ||
                (!isFree && !this.payReady)}
                aria-busy=${this.submitting ? "true" : "false"}
                @click=${() => void this.submitPurchase(isFree)}
              >
                ${this.submitting ? "Processing…" : cta}
              </button>
            </div>`
          : nothing}
      </div>
    `;
  }

  /**
   * Render the provider-authored form, not a synthetic acknowledgment. A
   * configured form that cannot load stays visible as a retryable blocker.
   */
  private renderTerms(): TemplateResult | typeof nothing {
    if (!this.termsRequired) return nothing;
    if (this.formLoadState === "loading") {
      return html`<div class="waiver" aria-busy="true">
        <div class="waiver-body">Loading required purchase form…</div>
      </div>`;
    }
    if (this.formLoadState === "error") {
      return html`<div class="waiver">
        <div class="waiver-body" role="alert">
          The required purchase form could not be loaded. Please try again.
          <button
            type="button"
            class="add-link"
            @click=${() => void this.loadTermsForm()}
          >
            Retry form
          </button>
        </div>
      </div>`;
    }
    const f = this.termsForm;
    if (!f) return nothing;
    const authoredBody = [
      ...toParagraphs(f.details),
      ...f.questions.flatMap((question) =>
        toParagraphs(question.detailsHtml),
      ),
    ];
    const bodyParas = authoredBody.length
      ? authoredBody
      : DEFAULT_TERMS_PARAGRAPHS;
    return html`
      <div class="waiver">
        <button
          class="waiver-head"
          @click=${() => (this.termsOpen = !this.termsOpen)}
        >
          <span class="ic">${ICON_DOC}</span>
          <span style="flex:1;min-width:0">
            <span class="t" style="display:block"
              >${f.name || "Purchase terms & conditions"}</span
            >
            <span class="s"
              >${this.formOnFile
                ? "Completed previously — tap to review."
                : this.productFormValid
                  ? "Complete — tap to review."
                  : "Required — complete the fields below."}</span
            >
          </span>
          <span class="chev ${this.termsOpen ? "open" : ""}"
            >${ICON_CHEVRON}</span
          >
        </button>
        ${this.termsOpen
          ? html`<div class="waiver-body">
              ${bodyParas.map((p) => html`<p>${p}</p>`)}
              ${this.formOnFile
                ? html`<div class="promo-msg ok">This form is already on file.</div>`
                : html`<div class="waiver-fields">
                    ${f.questions.map((question) =>
                      this.renderProductFormQuestion(question),
                    )}
                  </div>`}
            </div>`
          : nothing}
      </div>
    `;
  }

  private get recipientName(): string {
    return `${this.recipient.firstName} ${this.recipient.lastName}`.trim();
  }

  /**
   * PLAT-5624: the single guarded entry point for the step-2 CTA.
   *
   * The slot is claimed synchronously — before the first `await` — so the
   * second half of a double-click is a no-op even though Lit re-renders the
   * `?disabled` button a microtask later. Prior to this, both clicks ran the
   * full charge and the buyer was billed twice.
   *
   * A pre-payment failure returns the actor to `editing` in `finally`; success
   * or paid fulfillment recovery remains non-chargeable.
   */
  private async submitPurchase(isFree: boolean): Promise<void> {
    if (!this.submission.claim()) return;
    let charged = false;
    try {
      if (isFree) {
        charged = await this.confirmFreePurchase();
        return;
      }
      if (this.paymentEl) {
        // <zipper-payment> owns its submission actor + idempotency key, so this only
        // has to keep the outer CTA inert for the duration of the charge.
        await this.paymentEl.submitPayment();
        charged = this.placed;
        return;
      }
      // No payment element yet (step 2 opened before the total was known) —
      // mount it; this does not charge, so the CTA re-enables for the real pay.
      await this.mountInlinePayment();
    } finally {
      this.submission.release(charged, this.payError);
    }
  }

  /** @returns true only when the $0 purchase was actually recorded. */
  private async confirmFreePurchase(): Promise<boolean> {
    this.teardownPayment();
    // Defence in depth: a gift card must never reach the ordinary $0 product
    // charge — that would bill the catalog placeholder and create no GiftCard.
    if (this.isGift) {
      this.payError = `Enter a gift card amount of at least ${fmtMoney(
        GIFT_MIN_CENTS / 100,
        this.currency,
      )} to continue.`;
      return false;
    }
    if (this.firstTimeClientsOnly && this.memberCtx?.hasPriorPurchases) {
      this.payError =
        "This offer is for first-time clients only. Choose a different product.";
      return false;
    }
    if (!this.stripeProductId) {
      this.payError = "This product is not available for purchase yet.";
      return false;
    }
    if (!this.detailsValid) {
      this.payError = "Complete your details before confirming.";
      return false;
    }
    let formResponseId: string | undefined;
    try {
      formResponseId = await this.ensureProductFormResponse();
    } catch {
      return false;
    }

    let purchasedForRosterId = 0;
    if (
      this.forSomeoneElse &&
      this.recipientType === "family" &&
      this.selectedFamilyRosterId > 0
    ) {
      purchasedForRosterId = this.selectedFamilyRosterId;
    } else if (
      this.forSomeoneElse &&
      (this.recipientType === "guest" || this.recipientType === "family") &&
      this.recipient.firstName.trim() &&
      this.recipient.lastName.trim()
    ) {
      try {
        const guest = await createGuestRoster(
          this.orgId,
          {
            firstName: this.recipient.firstName,
            lastName: this.recipient.lastName,
            email: this.recipient.email || this.buyer.email,
            // The recipient of the purchase, not the buyer — see
            // `isPrimaryCustomer`.
            isPrimaryCustomer: false,
          },
          ...runtimeApiArguments(this.runtime),
        );
        purchasedForRosterId = guest.rosterId;
      } catch (e) {
        // "Try again" is a lie when the recipient's email already belongs to a
        // member here: create-guest will 401 every time. Name the address we
        // recognised and give the two things that do work.
        if (isAccountExistsError(e)) {
          this.payError = accountExistsForOtherMessage(
            this.recipient.email || this.buyer.email,
            `${this.recipient.firstName} ${this.recipient.lastName}`.trim(),
          );
          return false;
        }
        this.payError = "Could not set up the guest profile. Please try again.";
        return false;
      }
    }

    this.payError = "";
    try {
      await runFreePurchase({
        orgId: this.orgId,
        guest: {
          ...this.buyer,
          // PLAT-4456: a $0 purchase still mints the guest roster, so the org's
          // required-signup-fields policy applies to it exactly as it does to a
          // paid one.
          ...requiredProfilePayload(this.requiredProfile),
        },
        items: [
          {
            stripeProductId: this.stripeProductId,
            amount: 0,
            quantity: 1,
            customDescription: this.productName,
            formResponseId,
          },
        ],
        memberContext: this.memberCtx ?? undefined,
        guestRoster: this.formGuestRoster ?? undefined,
        // PLAT-5624: $0 still grants a pack/membership — key it so a duplicate
        // delivery can't grant twice.
        chargeAttemptId: this.submission.attemptId,
        purchasedForRosterId: purchasedForRosterId || undefined,
        ...runtimeApiOption(this.runtime),
      });
      this.submission.succeed();
      this.dispatchEvent(
        new CustomEvent("zipper-purchase-complete", {
          detail: {
            productName: this.productName,
            total: this.deferredChargeSettings?.enabled ? 0 : this.total,
          },
          bubbles: true,
          composed: true,
        }),
      );
      return true;
    } catch (e) {
      this.payError =
        e instanceof Error ? e.message : "Could not complete your purchase.";
      return false;
    }
  }

  private async mountInlinePayment(): Promise<void> {
    await this.openPayment(true);
  }

  /**
   * Step 1 of the gift sequence: create the GiftCard row via
   * `POST /gift-cards/purchase-intent` and hold on to what it returns.
   *
   * This has to happen BEFORE the payment element mounts, not at submit time:
   * the element builds the Apple/Google Pay PaymentRequest from `items`, and a
   * wallet payment is authorized and charged from Stripe's own button without
   * ever calling back through `submitPurchase()`. Items set late would mean a
   * wallet buyer authorizing the wrong amount — the exact class of bug this
   * flow exists to prevent.
   *
   * Abandoning checkout here leaves a `paymentPending` GiftCard that is never
   * charged; `GiftCardCleanupCronService` soft-deletes those after 7 days.
   *
   * @returns the intent, or null when the server refused (the message is
   *   surfaced in `payError` and nothing is charged).
   */
  private async ensureGiftIntent(): Promise<GiftPurchaseIntent | null> {
    const cents = this.giftCents;
    const r = this.giftRecipient;
    const key = JSON.stringify([
      cents,
      r.firstName.trim(),
      r.lastName.trim(),
      r.email.trim().toLowerCase(),
      this.giftMessage,
      this.giftSendToRecipient,
      this.buyer.email.trim().toLowerCase(),
    ]);
    if (this.giftIntent && this.giftIntentKey === key) return this.giftIntent;
    try {
      const intent = await createGiftPurchaseIntent(
        {
          orgId: this.orgId,
          amountInCents: cents,
          senderFirstName: this.buyer.firstName.trim(),
          senderLastName: this.buyer.lastName.trim(),
          senderEmail: this.buyer.email.trim(),
          recipientFirstName: r.firstName.trim(),
          recipientLastName: r.lastName.trim(),
          recipientEmail: r.email.trim() || undefined,
          message: this.giftMessage.trim() || undefined,
          sendToRecipient: this.giftSendToRecipient,
        },
        ...runtimeApiArguments(this.runtime),
      );
      this.giftIntent = intent;
      this.giftIntentKey = key;
      return intent;
    } catch (e) {
      // Covers the org-level kill switch too: `giftCardSettings.enableGiftCards
      // === false` makes the endpoint 400 with "Gift cards are not available
      // for this organization", which is exactly what the buyer should read.
      this.payError =
        e instanceof Error
          ? e.message
          : "Could not start this gift card purchase.";
      return null;
    }
  }

  /**
   * Step 3 of the gift sequence: activate the paid card and email the claim.
   *
   * Deliberately best-effort. The money is already taken by the time this runs,
   * so a failure here must not surface as a failed purchase — and it does not
   * lose the gift: the Stripe invoice carries `metadata.giftCardId`, and the
   * `invoice.paid` webhook completes the card server-side if this never lands.
   */
  private async completeGift(invoiceId: string | undefined): Promise<void> {
    const giftCardId = this.giftIntent?.giftCardId;
    if (!giftCardId || !invoiceId) return;
    try {
      await completeGiftCard(
        this.orgId,
        giftCardId,
        invoiceId,
        this.giftSendToRecipient,
        ...runtimeApiArguments(this.runtime),
      );
    } catch {
      /* webhook safety net completes it from metadata.giftCardId */
    }
  }

  private async openPayment(inline = false): Promise<void> {
    this.payError = "";
    if (this.isGift) {
      await this.openGiftPayment(inline);
      return;
    }
    if (this.total <= 0 && !this.deferredChargeSettings?.enabled) {
      await this.confirmFreePurchase();
      return;
    }
    if (this.firstTimeClientsOnly && this.memberCtx?.hasPriorPurchases) {
      this.payError =
        "This offer is for first-time clients only. Choose a different product.";
      return;
    }
    if (!this.stripeProductId) {
      this.payError = "This product is not available for purchase yet.";
      return;
    }
    const recipientNote =
      this.forSomeoneElse && this.recipientType
        ? ` · for ${this.recipientName} (${this.recipientType})`
        : "";

    const el = document.createElement("zipper-payment") as ZipperPayment;
    el.runtime = this.runtime;
    el.inline = inline;
    el.stripe = this.stripe;
    el.orgId = this.orgId;
    el.accent = this.accent;
    el.requirePhone = this.requirePhone;
    el.requiredSignupFields = this.requiredSignupFields;
    el.currency = this.currency;
    el.heading = this.productName;
    el.items = [
      {
        stripeProductId: this.stripeProductId,
        deferredChargeSettings: this.deferredChargeSettings,
        // Charge full price; the coupon (if any) is applied invoice-wide by Stripe.
        amount: this.price,
        quantity: 1,
        customDescription: `${this.productName}${recipientNote}`,
      },
    ];
    el.beforeCharge = async () => {
      const responseId = await this.ensureProductFormResponse(el);
      this.bindProductFormResponse(el, responseId);
    };
    el.customer = {
      ...this.buyer,
      ...requiredProfilePayload(this.requiredProfile),
    };
    // Phase 4 (additive): offer the member's saved cards. Empty for guests.
    el.savedCards = this.memberCtx?.savedCards ?? [];
    el.memberContext = this.memberCtx ?? undefined;
    if (
      this.forSomeoneElse &&
      this.recipientType === "family" &&
      this.selectedFamilyRosterId > 0
    ) {
      el.purchasedForRosterId = this.selectedFamilyRosterId;
    } else if (
      this.forSomeoneElse &&
      (this.recipientType === "guest" || this.recipientType === "family") &&
      this.recipient.firstName.trim() &&
      this.recipient.lastName.trim()
    ) {
      try {
        const guest = await createGuestRoster(
          this.orgId,
          {
            firstName: this.recipient.firstName,
            lastName: this.recipient.lastName,
            email: this.recipient.email || this.buyer.email,
            // The person the purchase is FOR, not the buyer — the org's
            // required-signup-fields policy is asked of the buyer only, and
            // this form never collects it for the recipient. Matches the
            // free/credit path above.
            isPrimaryCustomer: false,
          },
          ...runtimeApiArguments(this.runtime),
        );
        el.purchasedForRosterId = guest.rosterId;
      } catch (e) {
        // Same dead end as the free path above — see the note there.
        if (isAccountExistsError(e)) {
          this.payError = accountExistsForOtherMessage(
            this.recipient.email || this.buyer.email,
            `${this.recipient.firstName} ${this.recipient.lastName}`.trim(),
          );
          return;
        }
        this.payError = "Could not set up the guest profile. Please try again.";
        return;
      }
    }
    // Next already saved the form; quote and charge share that same identity.
    this.bindProductFormResponse(el, this.formResponseId ?? undefined);
    // Let Stripe apply the coupon invoice-wide.
    el.couponId = this.appliedCouponId;
    el.discount = this.discount;
    // The payment step previews the charge; mirror its numbers in the summary
    // rather than previewing the same cart a second time.
    el.addEventListener("zipper-pay-quote", (e: Event) => {
      this.deferredPreview =
        (
          e as CustomEvent<{
            deferredPreview?: import("@zipper/embedded-api-client").DeferredMembershipPreviewDto;
          }>
        ).detail?.deferredPreview ?? null;
      this.quote =
        (e as CustomEvent<{ totals: OrderTotals | null }>).detail?.totals ??
        null;
    });
    el.addEventListener("zipper-pay-success", () => {
      this.teardownPayment();
      this.submission.markPaymentCommitted();
      this.submission.succeed();
      this.dispatchEvent(
        new CustomEvent("zipper-purchase-complete", {
          detail: {
            productName: this.productName,
            total: this.deferredChargeSettings?.enabled ? 0 : this.total,
          },
          bubbles: true,
          composed: true,
        }),
      );
    });
    el.addEventListener("zipper-pay-close", () => this.teardownPayment());
    // PLAT-5624: keep the outer CTA inert for charges this component did not
    // start itself — the Apple/Google Pay sheet submits from Stripe's own
    // button, so `submitPurchase()` never runs for a wallet payment.
    el.addEventListener("zipper-pay-busy", (e: Event) => {
      const detail = (e as CustomEvent<{ busy: boolean }>).detail;
      this.submission.setChildBusy(!!detail?.busy && !this.placed);
    });
    const host = inline
      ? (this.renderRoot.querySelector("#inline-pay-host") ?? document.body)
      : document.body;
    // Must be attached BEFORE appendChild: the component fires its first
    // zipper-pay-ready(false) on its first update.
    el.addEventListener("zipper-pay-ready", (e) => {
      this.payReady = (
        e as CustomEvent<{ canSubmit: boolean }>
      ).detail.canSubmit;
    });
    host.appendChild(el);
    this.paymentEl = el;
  }

  /**
   * Gift-card payment mount: purchase-intent first, then a payment element
   * pointed at the product and amount the SERVER returned.
   *
   * The catalog entry is never charged. Its price is either a placeholder the
   * buyer replaced, or a denomination the server re-prices anyway when the org
   * passes gift-card fees on to the buyer (`passOnGiftCardFees`) — so
   * `stripeProductId`/`chargeAmountInCents` from the intent are the only
   * correct inputs to the charge.
   */
  private async openGiftPayment(inline: boolean): Promise<void> {
    const giftErrs = this.giftErrors();
    if (giftErrs.length) {
      this.payError = giftErrs[0];
      return;
    }
    const intent = await this.ensureGiftIntent();
    if (!intent) return; // payError already set
    // Never fall back to the catalog product/price: without both of these the
    // charge would bill the placeholder and the gift would be unfunded.
    if (!intent.stripeProductId || !intent.chargeAmountInCents) {
      this.payError =
        "Gift cards are not set up for this business yet. Please contact them directly.";
      return;
    }

    const el = document.createElement("zipper-payment") as ZipperPayment;
    el.runtime = this.runtime;
    el.inline = inline;
    el.stripe = this.stripe;
    el.orgId = this.orgId;
    el.accent = this.accent;
    el.requirePhone = this.requirePhone;
    el.requiredSignupFields = this.requiredSignupFields;
    el.currency = this.currency;
    el.heading = this.productName;
    el.items = [
      {
        stripeProductId: intent.stripeProductId,
        amount: intent.chargeAmountInCents / 100,
        quantity: 1,
        customDescription: `Gift card for ${this.giftRecipientName}`,
      },
    ];
    // A gift buyer is still becoming a member here: this element mints their
    // guest roster, so it needs the org-required fields too.
    el.customer = {
      ...this.buyer,
      ...requiredProfilePayload(this.requiredProfile),
    };
    el.savedCards = this.memberCtx?.savedCards ?? [];
    el.memberContext = this.memberCtx ?? undefined;
    // No coupon and no `purchasedForRosterId`: a gift card's value is set by
    // the intent, and it is redeemed by the recipient's own claim rather than
    // granted to a roster at charge time.
    el.afterCharge = async (invoiceIds) => {
      await this.completeGift(invoiceIds[0]);
    };
    // The payment step previews the charge; mirror its numbers in the summary
    // rather than previewing the same cart a second time.
    el.addEventListener("zipper-pay-quote", (e: Event) => {
      this.quote =
        (e as CustomEvent<{ totals: OrderTotals | null }>).detail?.totals ??
        null;
    });
    el.addEventListener("zipper-pay-success", () => {
      this.teardownPayment();
      this.submission.markPaymentCommitted();
      this.submission.succeed({ orderId: intent.giftCardId });
      this.dispatchEvent(
        new CustomEvent("zipper-purchase-complete", {
          detail: {
            productName: this.productName,
            total: this.total,
            giftCardId: intent.giftCardId,
          },
          bubbles: true,
          composed: true,
        }),
      );
    });
    el.addEventListener("zipper-pay-close", () => this.teardownPayment());
    el.addEventListener("zipper-pay-busy", (e: Event) => {
      const detail = (e as CustomEvent<{ busy: boolean }>).detail;
      this.submission.setChildBusy(!!detail?.busy && !this.placed);
    });
    el.addEventListener("zipper-pay-ready", (e) => {
      this.payReady = (
        e as CustomEvent<{ canSubmit: boolean }>
      ).detail.canSubmit;
    });
    const host = inline
      ? (this.renderRoot.querySelector("#inline-pay-host") ?? document.body)
      : document.body;
    host.appendChild(el);
    this.paymentEl = el;
  }

  private get giftRecipientName(): string {
    return `${this.giftRecipient.firstName} ${this.giftRecipient.lastName}`.trim();
  }

  private teardownPayment(): void {
    this.paymentEl?.remove();
    this.paymentEl = null;
    this.payReady = false;
  }

  private renderSummary(): TemplateResult {
    return renderPurchaseSummary({
      image: this.image,
      productName: this.productName,
      productSub: this.productSub,
      price: this.price,
      recurring: this.recurring,
      isGift: this.isGift,
      isDeferred: !!this.deferredChargeSettings?.enabled,
      deferredPreview: this.deferredPreview ?? undefined,
      giftCents: this.giftCents,
      giftRecipientName: this.giftRecipientName,
      features: this.features,
      promoCode: this.promoCode,
      promoBusy: this.promoBusy,
      promoError: this.promoError,
      discount: this.discount,
      subtotal: this.subtotal,
      total: this.total,
      quote: this.quote ?? undefined,
      currency: this.currency,
      onPromoCodeChange: (value) => {
        this.promoCode = value;
        this.promoError = "";
      },
      onApplyPromo: () => void this.applyPromo(),
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-purchase-checkout": ZipperPurchaseCheckout;
  }
}
